import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSearch,
  faStore,
  faLocationDot,
  faUserTie,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";
import AILightComponent from "../../components/AILightComponent";

const HerKartStoresAroundMe = () => {
  const authState = useSelector((state) => state.authReducer);
  const audienceName= authState?.user?.name;
  const userGender=authState?.user.gender;
  const userAge=null;

  const navigate = useNavigate();
  const { fetchLiveStores } = useHerKart();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("All");

  const [speakingStoreId, setSpeakingStoreId] = useState(null);
  const [translateForId, setTranslateForId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetchLiveStores();
      if (response?.success) {
        setStores(response.stores || []);
      }
    } catch (e) {
      console.error("Stores fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  const cities = useMemo(() => {
    const unique = [...new Set(stores.map((s) => s.city).filter(Boolean))];
    return ["All", ...unique];
  }, [stores]);

  const filteredStores = useMemo(() => {
    let list = [...stores];

    if (cityFilter !== "All") {
      list = list.filter((s) => s.city === cityFilter);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((s) =>
        [s.storeName, s.ownerName, s.locality, s.city, s.storeHeadline]
          .join(" ")
          .toLowerCase()
          .includes(keyword)
      );
    }

    return list;
  }, [stores, search, cityFilter]);

  const handleToggleSpeak = (e, store) => {
    e.stopPropagation();
    if (!store?.tts || !("speechSynthesis" in window)) return;

    if (speakingStoreId === store._id) {
      window.speechSynthesis.cancel();
      setSpeakingStoreId(null);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(store.tts);
    utterance.lang = "en-IN";
    utterance.onend = () => setSpeakingStoreId(null);
    utterance.onerror = () => setSpeakingStoreId(null);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setSpeakingStoreId(store._id);
  };

  const handleToggleTranslate = (e, storeId) => {
    e.stopPropagation();
    setTranslateForId((prev) => (prev === storeId ? null : storeId));
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/foundher"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 transition"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-xl font-bold">Stores Around Me</h1>
              <p className="text-xs text-slate-400">
                Discover private stores from women entrepreneurs
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-5"></div>
      <div className="mx-auto max-w-7xl px-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by store name, owner, or locality..."
              className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-pink-500 focus:outline-none"
            />
          </div>

          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <div className="flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-3 py-2 text-xs text-pink-300 whitespace-nowrap">
              <FontAwesomeIcon icon={faLocationDot} />
              City
            </div>

            {cities.map((city) => (
              <button
                key={city}
                onClick={() => setCityFilter(city)}
                className={`whitespace-nowrap rounded-full w-fit px-2 py-1 text-xs lg:text-sm transition ${
                  cityFilter === city
                    ? "bg-pink-600 text-white"
                    : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
              >
                {city}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-16 text-center text-slate-400">
            Loading stores...
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon
              icon={faStore}
              className="mx-auto mb-4 text-4xl text-slate-500"
            />
            <h3 className="text-xl font-semibold">No stores found</h3>
            <p className="mt-2 text-sm text-slate-400">
              {stores.length === 0
                ? "No stores have been created yet. Check back soon!"
                : "Try changing your search or city filter."}
            </p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredStores.map((store) => (
              <div
                key={store._id}
                onClick={() => navigate(`/herkart/products/${store.storeCode}`)}
                className="cursor-pointer rounded-3xl border border-white/10 bg-white/5 p-5 transition hover:border-pink-500/40"
              >
                <img
                  src={
                    store.storeImage ||
                    "https://placehold.co/400x250/18181b/f9a8d4?text=Store"
                  }
                  alt={store.storeName}
                  className="mb-4 h-40 w-full rounded-2xl object-cover"
                />

                <h3 className="text-lg font-bold text-cyan-200">
                  {store.storeName}
                </h3>

                {store.storeHeadline && (
                  <p className="mt-1 text-sm text-slate-300 line-clamp-2">
                    {store.storeHeadline}
                  </p>
                )}

                {store.ownerName && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                    <FontAwesomeIcon icon={faUserTie} className="text-pink-400" />
                    <span className="truncate">{store.ownerName}</span>
                  </div>
                )}

                {(store.locality || store.city) && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                    <FontAwesomeIcon icon={faLocationDot} className="text-pink-400" />
                    <span>
                      {store.locality}
                      {store.locality && store.city ? ", " : ""}
                      {store.city}
                    </span>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full bg-pink-600/20 px-3 py-1 text-xs font-semibold text-pink-300">
                    {store.storeCode}
                  </div>

                  {store.tts && (
                    <button
                      type="button"
                      onClick={(e) => handleToggleSpeak(e, store)}
                      title={speakingStoreId === store._id ? "Stop" : "Listen to store introduction"}
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all ${
                        speakingStoreId === store._id
                          ? "bg-pink-500 text-white"
                          : "bg-white/10 text-pink-300 hover:bg-white/20"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={speakingStoreId === store._id ? faVolumeXmark : faVolumeHigh}
                      />
                    </button>
                  )}
                </div>

                {store.tts && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={(e) => handleToggleTranslate(e, store._id)}
                      className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
                        translateForId === store._id
                          ? "bg-purple-500 text-white"
                          : "bg-[#111827] text-purple-300 border border-purple-500/30"
                      }`}
                    >
                      {translateForId === store._id ? "🙈 Hide" : "🌐 Listen in Your Language"}
                    </button>

                    {translateForId === store._id && (
                      <div
                        className="mt-2 w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <AILightComponent
                          mode="herkart_store_translate_story"
                          param1={"🌐 Hear This Store's Story in Your Language"}
                          param2={`
Store Name: ${store.storeName || "N/A"}
Store Headline/Tagline: ${store.storeHeadline || "N/A"}
Owner/Entrepreneur: ${store.ownerName || "N/A"}
Location: ${store.locality || ""} ${store.city || ""}
Original Spoken Introduction (in English): ${store.tts}

Your task:
Translate and naturally retell the "Original Spoken Introduction" above into the selected output language, so it sounds like it was originally written and spoken in that language by the store owner herself — not a literal, robotic translation.

STRICT RULES:
1. Preserve the warmth, personal tone, and story-like feel of the original — this is a welcome message from a woman entrepreneur to a customer, not a product description.
2. Do NOT invent new facts, names, or details not present above. Stay faithful to what's given.
3. Make it sound natural and fluent in the target language — adapt idioms and phrasing rather than translating word-for-word.
4. Output ONLY the final translated spoken text — nothing else. No headings, labels, meta-commentary, markdown, quotation marks, or explanations.
5. Keep it close to the original length — suitable for a Text-to-Speech voice assistant to read aloud in one breath.

Return only the translated spoken introduction, ready to be read aloud or pasted directly.
`}
                            audienceGender={userGender}
                            audienceAge={userAge}
                            audienceName={audienceName}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HerKartStoresAroundMe;